import { logError, logWarning } from '@databases/logs';
// FIXME
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux';
import { type ObjectMode, type Optional } from '@databases/types';
import {
  type BlogpostReferenceOrContentId,
  type PageReferenceOrContentId,
} from '@databases/types/confluence';
import {
  type DatabaseAttachment,
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue,
  type EntryWithContext,
  type FieldWithContext,
  isDatabaseTextValue,
  type RawDatabaseValue,
  type RawValueWithContext,
  type RenderContext,
  type ValueWithContext,
} from '@databases/types/database';
import {
  type DatabaseFieldFilterType,
  type DatabaseFilterType,
} from '@databases/types/database/view';
import { type Cheerio } from 'cheerio';
import { type Element } from 'domhandler';
import { type IntlShape } from 'react-intl-next';

import { RECURSION_LIMIT } from '../constants';
import {
  type ActionDependencyHelpers,
  runActionWithDependencies,
  type SimpleStore,
} from '../redux/action-dependencies';
import { selectDatabaseByContentIdOptional } from '../redux/databases/selectors';

import attachmentFieldType from './field-types/attachment';
import backlinkFieldType from './field-types/backlink';
import checkboxFieldType from './field-types/checkbox';
import dateFieldType from './field-types/date';
import entryDetailsFieldType from './field-types/entryDetails';
import entryLinkFieldType from './field-types/entryLink';
import hyperlinkFieldType from './field-types/hyperlink';
import jiraIssueFieldType from './field-types/jiraIssue';
import jiraIssueDetailsFieldType, {
  getJiraColumnTypeFromState,
  getJiraFieldFilterTypes,
  type JiraIssuesState,
} from './field-types/jiraIssueDetails';
import numberFieldType from './field-types/number';
import pageDetailsFieldType from './field-types/pageDetails';
import pageLabelsFieldType from './field-types/pageLabels';
import pageLinkFieldType from './field-types/pageLink';
import pageStatusFieldType from './field-types/pageStatus';
import selectFieldType from './field-types/select';
import textFieldType from './field-types/text';
import userFieldType from './field-types/user';

export {
  selectEquivalentFieldType,
  selectFirstResolvedJiraIssue,
  selectJiraColumnOptions,
  selectJiraFieldInfo,
  selectJiraIssueContext,
  type JiraColumnOption,
  type JiraFieldInfo,
  type JiraIssueContext,
  type JiraIssuesState,
} from './field-types/jiraIssueDetails';

export interface LoadDependenciesHelpers
  extends ActionDependencyHelpers<SimpleStore<DefaultRootState>> {
  selectDatabase: typeof selectDatabaseByContentIdOptional;
  /** A recursive reference to the object itself, to make it easier to pass it on to recursive loadDependencies() calls. */
  helpers: this;
  intl: IntlShape;
}

export interface FieldType<Type extends DatabaseFieldType> {
  /**
   * Which database filter types this field type supports. For example, for a text field it does not make sense to define a
   * "greater than" filter, and for an image field a "contains" filter does not make sense.
   */
  supportedFilterTypes: DatabaseFieldFilterType[];

  /**
   * Whether the field type supports CSV import.
   */
  isCsvImportSupported: boolean;

  /**
   * Render the value in storage format, to be rendered by a static macro.
   * Note that many features of the Confluence UI are not available here. For example, a link with ac:card-appearance="inline"
   * would be rendered as a smart link when saved on a Confluence page, but when rendered by a static macro it is rendered as
   * a simple link.
   *
   * The copy/paste code path needs to be synchronous. For this reason, this function should be called when relating to
   * copy/paste. To keep the code path synchronous, it will not fetch the media url. If the url is needed in the html,
   * for example for the export view, renderExportViewAsync should be called.
   * @param recursionCount When making calls to any other function that has a recursionCount argument, pass this value increased by 1.
   */
  renderExportView: (
    context: RawValueWithContext<Type, ObjectMode>,
    state: DefaultRootState,
    renderContext: RenderContext,
    recursionCount: number,
    contentId: string
  ) => string;

  /**
   * In the attachment field type, the media url needs to be fetched. For this reason, an async version of
   * renderExportView is needed.
   */
  renderExportViewAsync?: (
    context: RawValueWithContext<Type, ObjectMode>,
    state: DefaultRootState,
    contentId: string
  ) => Promise<string>;

  /**
   * Attempt to parse a value from a pasted HTML table cell.
   * Ideally, a field type should be able to parse values from its own export format.
   * @param el A HTML table cell (td or th)
   */
  parseFromHtml?: (
    el: Cheerio<Element>,
    context: FieldWithContext<Type, ObjectMode> & EntryWithContext<ObjectMode>,
    state: DefaultRootState
  ) => RawDatabaseValue<Type>;

  /**
   * Get the empty value for this field type. It is used as a fallback whenever a value is not defined, for example for existing
   * entries when creating a new field.
   */
  getInitialValue: () => RawDatabaseValue<Type>;

  /**
   * Gets the initial default value that is set when creating a new field of this type. If this function is not defined,
   * getInitialValue() is used.
   */
  getInitialDefaultValue?: () => RawDatabaseValue<Type>;

  /** Return true if the given value represents that the field is empty. */
  isEmptyValue: (
    context: Optional<
      RawValueWithContext<Type, ObjectMode.UPDATE | ObjectMode.EXISTING, Type>,
      'entry'
    >,
    state: DefaultRootState | undefined,
    recursionCount: number
  ) => boolean;

  /** Return true if the two values are equal. */
  isEqualValue: (
    context1: Optional<
      RawValueWithContext<Type, ObjectMode.UPDATE | ObjectMode.EXISTING, Type>,
      'entry'
    >,
    context2: Optional<
      RawValueWithContext<Type, ObjectMode.UPDATE | ObjectMode.EXISTING, Type>,
      'entry'
    >,
    state: DefaultRootState | undefined,
    recursionCount: number
  ) => boolean;

  /**
   * Get a string representation of the value to be used in CSV export.
   * @param recursionCount When making calls to any other function that has a recursionCount argument, pass this value increased by 1.
   */
  getCsvRepresentation?: (
    context: RawValueWithContext<Type, ObjectMode.UPDATE | ObjectMode.EXISTING, Type>
  ) => string | undefined;

  /**
   * Get a string representation of the value, to be shown for example in an entry picker, to be used for sorting and to be used
   * for the "contains" filter.
   * @param recursionCount When making calls to any other function that has a recursionCount argument, pass this value increased by 1.
   */
  getStringRepresentation: (
    context: Optional<
      RawValueWithContext<Type, ObjectMode.UPDATE | ObjectMode.EXISTING, Type>,
      'entry'
    >,
    state: DefaultRootState | undefined,
    recursionTracker: { recursionCount: number },
    defaultEntryName?: string
  ) => string | undefined;

  /**
   * Get a number representation of the value, to be used for the "greater than" and "less than" filters and for
   * sorting. If not applicable for the value, can return undefined. In that case these filters never match the value.
   * @param recursionCount When making calls to any other function that has a recursionCount argument, pass this value increased by 1.
   */
  getNumberRepresentation?: (
    context: Optional<
      RawValueWithContext<Type, ObjectMode.UPDATE | ObjectMode.EXISTING, Type>,
      'entry'
    >,
    state: DefaultRootState | undefined,
    recursionCount: number
  ) => number | undefined;

  /**
   * Returns the database attachments that are referenced by this value. Attachments that are not referenced by any value
   * will be deleted when updating the database content.
   */
  getReferencedAttachments?: (
    context: RawValueWithContext<Type, ObjectMode>,
    state: DefaultRootState | undefined
  ) => DatabaseAttachment[];

  /**
   * Returns a list of pages and blogposts that are referenced by this value (that is, this value serializes to an <ri:page> reference
   * to that page or to an <ri:blog-post> reference to that blogpost).
   * This is used to determine whether the database has to be reloaded when a particular page/blogpost is renamed/moved.
   */
  getReferencedContent?: (
    context: RawValueWithContext<Type, ObjectMode>,
    state: DefaultRootState | undefined
  ) => Array<PageReferenceOrContentId | BlogpostReferenceOrContentId>;

  /**
   * Loads any dependencies that the database value depends on into the redux store. Many methods of the database field
   * implementations take a state argument, which they can use to resolve data from external dependencies. For example,
   * an entry link value persists the referenced entry title, but the title might have changed since the database has changed,
   * so the renderExportView, getStringRepresentation etc. methods check if the referenced database
   * is loaded into the store, and if it is use the current referenced entry title instead of the persisted one. This method
   * would make sure that the referenced database is loaded into the store.
   * Read calls to the state must be wrapped in helpers.selector() to make sure that the frontend can rerun this function
   * whenever any of the selector results change. Selecting a database from the store must be done using helpers.selectDatabase(),
   * as some parts of the frontend override this function to select a database draft.
   * @param recursionCount When making calls to any other function that has a recursionCount argument, pass this value increased by 1.
   */
  loadDependencies?: (
    context: {
      contentId: string;
      field: DatabaseField<Type>;
      entryId?: string;
      value: RawDatabaseValue<Type>;
    },
    helpers: LoadDependenciesHelpers,
    state: DefaultRootState | undefined,
    recursionTracker: { recursionCount: number }
  ) => void;

  /**
   * Converts a CSV text value to the desired field type.
   */
  // TODO NCTD-2806 remove once we rewrite import logic
  convertFromCsv?: (
    context: Omit<RawValueWithContext<DatabaseFieldType.TEXT>, 'database' | 'entry'>
  ) => RawDatabaseValue<Type>;

  /**
   * When changing the type of a field, this method is used to convert the values from one type to another. Only supported conversions
   * should be implemented, in case of unsupported conversions (for example converting a text field to an image field) the values are
   * reset to the initial value.
   * Note: Only convertFrom or convertTo should be defined for each conversion, not both.
   */
  convertFrom?: {
    [Type2 in Exclude<DatabaseFieldType, Type>]?: (
      context: Omit<RawValueWithContext<Type2>, 'database' | 'entry'>
    ) => RawDatabaseValue<Type>;
  };

  /**
   * When changing the type of a field, this method is used to convert the values from one type to another. Only supported conversions
   * should be implemented, in case of unsupported conversions (for example converting a text field to an image field) the values are
   * reset to the initial value.
   * Note: Only convertFrom or convertTo should be defined for each conversion, not both.
   * @param recursionCount When making calls to any other function that has a recursionCount argument, pass this value increased by 1.
   */
  convertTo?: {
    [Type2 in Exclude<DatabaseFieldType, Type>]?: (
      context: Omit<RawValueWithContext<Type>, 'database' | 'entry'>,
      state: DefaultRootState | undefined,
      recursionCount: number
    ) => RawDatabaseValue<Type2>;
  };

  /**
   * If this is true, the UI won't allow a default value to be specified for fields of this type. At the moment this should be used
   * for field types that rely on attachments, since there is no good way to copy attachments from the database CCEO to the
   * database entries CCEO at the moment.
   */
  noDefaultValue?: boolean;
}

const fieldTypes: { [type in DatabaseFieldType]: FieldType<type> } = {
  // Using getters to avoid cyclic dependencies
  get [DatabaseFieldType.TEXT]() {
    return textFieldType;
  },
  get [DatabaseFieldType.NUMBER]() {
    return numberFieldType;
  },
  get [DatabaseFieldType.CHECKBOX]() {
    return checkboxFieldType;
  },
  get [DatabaseFieldType.PAGE_LINK]() {
    return pageLinkFieldType;
  },
  get [DatabaseFieldType.ATTACHMENT]() {
    return attachmentFieldType;
  },
  get [DatabaseFieldType.USER]() {
    return userFieldType;
  },
  get [DatabaseFieldType.DATE]() {
    return dateFieldType;
  },
  get [DatabaseFieldType.SELECT]() {
    return selectFieldType;
  },
  get [DatabaseFieldType.HYPERLINK]() {
    return hyperlinkFieldType;
  },
  get [DatabaseFieldType.JIRA_ISSUE]() {
    return jiraIssueFieldType;
  },
  get [DatabaseFieldType.ENTRY_LINK]() {
    return entryLinkFieldType;
  },
  get [DatabaseFieldType.ENTRY_DETAILS]() {
    return entryDetailsFieldType;
  },
  get [DatabaseFieldType.BACKLINK]() {
    return backlinkFieldType;
  },
  get [DatabaseFieldType.PAGE_LABELS]() {
    return pageLabelsFieldType;
  },
  get [DatabaseFieldType.PAGE_STATUS]() {
    return pageStatusFieldType;
  },
  get [DatabaseFieldType.PAGE_DETAILS]() {
    return pageDetailsFieldType;
  },
  get [DatabaseFieldType.JIRA_ISSUE_DETAILS]() {
    return jiraIssueDetailsFieldType;
  },
};

const untypedFieldTypes = fieldTypes as Record<
  DatabaseFieldType,
  FieldType<DatabaseFieldType>
>;

/**
 * Get the supported database filter types for the specified field type.
 */
export function getSupportedFilterTypes(type: DatabaseFieldType): DatabaseFilterType[] {
  return untypedFieldTypes[type].supportedFilterTypes;
}

/**
 * Get the supported database filter types for a field, with special handling for
 * Jira issue details fields that determines filter types based on the underlying
 * Jira column type (e.g., datetime fields get date-like filters).
 *
 * @param field The database field to get filter types for
 * @param jiraIssues Jira issues state used to look up Jira column types
 * @returns Array of supported filter types for this field
 */
export function getSupportedFilterTypesForField(
  field: DatabaseField,
  jiraIssues?: JiraIssuesState,
  isJiraTypeMappingEnabled = true
): DatabaseFilterType[] {
  if (field.type !== DatabaseFieldType.JIRA_ISSUE_DETAILS) {
    return getSupportedFilterTypes(field.type);
  }

  if (!isJiraTypeMappingEnabled) {
    return getSupportedFilterTypes(field.type);
  }

  const jiraColumnType = getJiraColumnTypeFromState(
    field as DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>,
    jiraIssues
  );

  if (jiraColumnType) {
    return getJiraFieldFilterTypes(jiraColumnType);
  }

  return getSupportedFilterTypes(field.type);
}

/**
 * Renders the given database value as storage format, for example to be displayed by a static macro.
 */
export function renderExportView(
  context: ValueWithContext<DatabaseFieldType, ObjectMode>,
  state: DefaultRootState,
  renderContext: RenderContext,
  recursionCount = 0,
  contentId: string
): string {
  const converted = convertType(context, context.field.type, state);
  return untypedFieldTypes[converted.type].renderExportView(
    { ...context, value: converted.value },
    state,
    renderContext,
    recursionCount,
    contentId
  );
}

/**
 * Renders the given database value as storage format, for example to be displayed by a static macro.
 */
export async function renderExportViewAsync(
  context: ValueWithContext<DatabaseFieldType, ObjectMode>,
  state: DefaultRootState,
  renderContext: RenderContext,
  recursionCount = 0,
  contentId: string
): Promise<string> {
  const converted = convertType(context, context.field.type, state);

  const fieldType = untypedFieldTypes[converted.type];

  if (fieldType.renderExportViewAsync) {
    return await fieldType.renderExportViewAsync(
      { ...context, value: converted.value },
      state,
      contentId
    );
  } else {
    return fieldType.renderExportView(
      { ...context, value: converted.value },
      state,
      renderContext,
      recursionCount,
      contentId
    );
  }
}

/**
 * Returns true if the given field type can import values from a CSV.
 */
export function isCsvImportSupported(type: DatabaseFieldType): boolean {
  return untypedFieldTypes[type].isCsvImportSupported;
}

/**
 * Attempts to parse a database value from the given pasted HTML table cell.
 * @param el A HTML table cell (td or th)
 */
export function parseFromHtml<Type extends DatabaseFieldType>(
  el: Cheerio<Element>,
  context: FieldWithContext<Type> & EntryWithContext<ObjectMode>,
  state: DefaultRootState
): DatabaseValue<Type> & { isSourceEmpty?: boolean } {
  const parseFromHtmlForFieldType = untypedFieldTypes[context.field.type].parseFromHtml;
  if (parseFromHtmlForFieldType) {
    return {
      type: context.field.type,
      value: parseFromHtmlForFieldType(el, context, state),
      isSourceEmpty: el.find('span').attr('data-is-empty') === 'true',
    } as any;
  } else {
    return getInitialValue(context.field.type);
  }
}

/**
 * Returns the initial value for the given field type. The initial value should be used as a fallback for a database value
 * that is not defined, for example in existing entries when a new field is created.
 */
export function getInitialValue<Type extends DatabaseFieldType>(
  type: Type
): DatabaseValue<Type> {
  return { type, value: untypedFieldTypes[type].getInitialValue() } as any;
}

/**
 * Returns the initial default value for the given field type. It should be used as the default value for newly created
 * fields.
 */
export function getInitialDefaultValue<Type extends DatabaseFieldType>(
  type: Type
): DatabaseValue<Type> {
  return {
    type,
    value: (
      untypedFieldTypes[type].getInitialDefaultValue ??
      untypedFieldTypes[type].getInitialValue
    )(),
  } as any;
}

/**
 * Returns true if the given database value represents an empty value of its field type (so that a placeholder can
 * be shown for example).
 */
export function isEmptyValue(
  context: Optional<
    ValueWithContext<DatabaseFieldType, ObjectMode.UPDATE | ObjectMode.EXISTING>,
    'entry'
  >,
  state: DefaultRootState | undefined,
  recursionCount = 0
): boolean {
  return untypedFieldTypes[context.value.type].isEmptyValue(
    { ...context, value: context.value.value },
    state,
    recursionCount
  );
}

const JIRA_EQUIVALENT_TYPES = [
  DatabaseFieldType.TEXT,
  DatabaseFieldType.SELECT,
  DatabaseFieldType.DATE,
  DatabaseFieldType.NUMBER,
  DatabaseFieldType.USER,
];

type ValueContext = Optional<
  ValueWithContext<DatabaseFieldType, ObjectMode.UPDATE | ObjectMode.EXISTING>,
  'entry'
>;

function tryJiraIsEqualValue(
  context1: ValueContext,
  context2: ValueContext,
  state: DefaultRootState | undefined,
  recursionCount: number
): boolean | undefined {
  if (
    context1.value.type === DatabaseFieldType.JIRA_ISSUE_DETAILS &&
    JIRA_EQUIVALENT_TYPES.includes(context2.value.type)
  ) {
    return untypedFieldTypes[DatabaseFieldType.JIRA_ISSUE_DETAILS].isEqualValue(
      { ...context1, value: context1.value.value },
      {
        ...context2,
        entry: undefined,
        value: isDatabaseTextValue(context2.value.value)
          ? context2.value.value.plain
          : context2.value.value,
      },
      state,
      recursionCount
    );
  }
  return undefined;
}

/**
 * Returns true if the given two database values are equal.
 */
export function isEqualValue(
  context1: ValueContext,
  context2: ValueContext,
  state: DefaultRootState | undefined,
  recursionCount = 0
): boolean {
  if (context1.value.type !== context2.value.type) {
    const jiraResult =
      tryJiraIsEqualValue(context1, context2, state, recursionCount) ??
      tryJiraIsEqualValue(context2, context1, state, recursionCount);
    if (jiraResult !== undefined) {
      return jiraResult;
    }
    return isEmptyValue(context1, state) && isEmptyValue(context2, state);
  }
  return untypedFieldTypes[context1.value.type].isEqualValue(
    { ...context1, value: context1.value.value },
    { ...context2, value: context2.value.value },
    state,
    recursionCount
  );
}

/**
 * Returns true if the given two lists of database values are equal, respecting their order. They are considered equal if
 * they contain the same amount of values, and for each value in each list the corresponding value at the same index in
 * the other list returns true for isEqualValue().
 */
export function isEqualValueList(
  contexts1: Array<
    Optional<
      ValueWithContext<DatabaseFieldType, ObjectMode.UPDATE | ObjectMode.EXISTING>,
      'entry'
    >
  >,
  contexts2: Array<
    Optional<
      ValueWithContext<DatabaseFieldType, ObjectMode.UPDATE | ObjectMode.EXISTING>,
      'entry'
    >
  >,
  state: DefaultRootState | undefined,
  recursionCount = 0
): boolean {
  if (contexts1.length !== contexts2.length) {
    return false;
  }

  return contexts1.every((context1, idx) =>
    isEqualValue(context1, contexts2[idx], state, recursionCount)
  );
}

/**
 * Returns a string representation of the given database value to be used in
 * export to CSV.
 */
export function getCsvRepresentation(
  context: ValueWithContext,
  state: DefaultRootState | undefined,
  recursionTracker = { recursionCount: 0 }
): string | undefined {
  if (recursionTracker.recursionCount < RECURSION_LIMIT) {
    const converted = convertType(
      context,
      context.field.type,
      state,
      getInitialValue(context.field.type),
      recursionTracker
    );

    const fieldType = untypedFieldTypes[converted.type];
    if (fieldType.getCsvRepresentation) {
      return fieldType.getCsvRepresentation({ ...context, value: converted.value });
    } else {
      return fieldType.getStringRepresentation(
        { ...context, value: converted.value },
        state,
        { recursionCount: recursionTracker.recursionCount }
      );
    }
  } else {
    logError(new Error('too many nested databases'));
    return '';
  }
}

/**
 * Returns a string representation of the given database value. The string representation can be used to show the
 * database value in a dropdown, or to sort a list of database values alphabetically.
 */
export function getStringRepresentation(
  context: Optional<ValueWithContext, 'entry'>,
  state: DefaultRootState | undefined,
  recursionTracker = { recursionCount: 0 }
): string | undefined {
  if (recursionTracker.recursionCount < RECURSION_LIMIT) {
    const converted = convertType(
      context,
      context.field.type,
      state,
      getInitialValue(context.field.type),
      recursionTracker
    );
    return untypedFieldTypes[converted.type].getStringRepresentation(
      { ...context, value: converted.value },
      state,
      { recursionCount: recursionTracker.recursionCount }
    );
  } else {
    logError(new Error('too many nested databases'));
    return '';
  }
}

/**
 * Returns a number representation of the given database value. The number representation can be used for the
 * "less than" and "greater than" database filters and for sorting. If undefined is returned, the value cannot
 * be represented as a number.
 */
export function getNumberRepresentation(
  context: ValueWithContext,
  state: DefaultRootState | undefined,
  recursionCount = 0
): number | undefined {
  const converted = convertType(context, context.field.type, state);
  try {
    const isJiraFilter =
      context.field.type === DatabaseFieldType.JIRA_ISSUE_DETAILS &&
      JIRA_EQUIVALENT_TYPES.includes(context.value.type);

    if (isJiraFilter) {
      const filterString = isDatabaseTextValue(context.value.value)
        ? context.value.value.plain
        : typeof context.value.value === 'string'
        ? context.value.value
        : undefined;

      if (filterString === undefined || filterString === '') {
        return undefined;
      }
      return untypedFieldTypes[
        DatabaseFieldType.JIRA_ISSUE_DETAILS
      ].getNumberRepresentation?.(
        { ...context, entry: undefined, value: filterString },
        state,
        recursionCount
      );
    }
    return untypedFieldTypes[context.value.type].getNumberRepresentation?.(
      { ...context, value: converted.value },
      state,
      recursionCount
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      logWarning(error);
    }
    return undefined;
  }
}

/**
 * Returns a list of pages and blogposts that are referenced by the given database value.
 */
export function getReferencedContent(
  context: ValueWithContext<DatabaseFieldType, ObjectMode>,
  state: DefaultRootState | undefined
): Array<PageReferenceOrContentId | BlogpostReferenceOrContentId> {
  return (
    untypedFieldTypes[context.value.type].getReferencedContent?.(
      { ...context, value: context.value.value },
      state
    ) ?? []
  );
}

/**
 * Loads any dependencies that the database value depends on into the redux store. Many methods of the database field
 * implementations take a state argument, which they can use to resolve data from external dependencies. For example,
 * an entry link value persists the referenced entry title, but the title might have changed since the database has changed,
 * so the renderExportView, getStringRepresentation etc. methods check if the referenced database
 * is loaded into the store, and if it is use the current referenced entry title instead of the persisted one. This method
 * would make sure that the referenced database is loaded into the store.
 */
export function loadDependencies(
  context: {
    contentId: string;
    field: DatabaseField;
    entryId?: string;
    value: DatabaseValue;
  },
  helpers: Omit<LoadDependenciesHelpers, 'helpers'>,
  state: DefaultRootState | undefined,
  recursionTracker = { recursionCount: 0 }
): void {
  const recursiveHelpers = { ...helpers } as LoadDependenciesHelpers;
  recursiveHelpers.helpers = recursiveHelpers;

  if (recursionTracker.recursionCount < RECURSION_LIMIT) {
    untypedFieldTypes[context.value.type].loadDependencies?.(
      { ...context, value: context.value.value },
      recursiveHelpers,
      state,

      recursionTracker
    );
    if (context.value.type !== context.field.type) {
      untypedFieldTypes[context.field.type].loadDependencies?.(
        {
          ...context,
          value: convertType(
            { field: context.field, value: context.value },
            context.field.type,
            state
          ).value,
        },
        recursiveHelpers,
        state,
        recursionTracker
      );
    }
  }
}

/**
 * Like {@link loadDependencies}, but returns a promise that is only resolved when all dependencies are loaded.
 */
export async function loadDependenciesAsync(
  context: {
    contentId: string;
    field: DatabaseField;
    entryId?: string;
    value: DatabaseValue;
  },
  store: SimpleStore<DefaultRootState>,
  intl: IntlShape,
  recursionCount = 0
): Promise<void> {
  await runActionWithDependencies((helpers) => {
    loadDependencies(
      context,
      {
        ...helpers,
        selectDatabase: selectDatabaseByContentIdOptional,
        intl,
      },
      store.getState(),
      { recursionCount }
    );
  }, store);
}

/**
 * Convert the given database value to the given type. If the value already has the right type, it is returned unchanged.
 * Not all field types can be converted to all others. In case no conversion is possible, the fallback is returned.
 * @param context The database value to convert.
 * @param newType The type to convert the value to.
 * @param state The redux state, to be used for resolving dependencies.
 * @param fallback The value to return in case no conversion is possible. Defaults to the initial value of the target type.
 * @param recursionTracker A helper object to keep track of the recursion count.
 */
export function convertType<NewType extends DatabaseFieldType>(
  context: Optional<
    ValueWithContext<DatabaseFieldType, ObjectMode>,
    'database' | 'entry'
  >,
  newType: NewType,
  state: DefaultRootState | undefined,
  fallback: DatabaseValue<NewType> = getInitialValue(newType),
  recursionTracker = { recursionCount: 0 }
): DatabaseValue<NewType> {
  if (context.value.type === newType) {
    return context.value as any;
  } else if ((untypedFieldTypes as any)[newType].convertFrom?.[context.value.type]) {
    return {
      type: newType,
      value: (untypedFieldTypes as any)[newType].convertFrom[context.value.type]({
        ...context,
        value: context.value.value,
      }),
    } as any;
  } else if ((untypedFieldTypes as any)[context.value.type].convertTo?.[newType]) {
    return {
      type: newType,
      value: (untypedFieldTypes as any)[context.value.type].convertTo[newType](
        { ...context, value: context.value.value },
        state,
        recursionTracker.recursionCount
      ),
    } as any;
  } else {
    return { ...fallback };
  }
}

/**
 * Convert text values to the desired field type in CSV import. If the value already has the right type, it is returned unchanged.
 * Not all field types are supported in CSV import. In case no conversion is possible, the fallback is returned.
 * @param context The database value to convert.
 * @param newType The type to convert the value to.
 * @param state The redux state, to be used for resolving dependencies.
 * @param fallback The value to return in case no conversion is possible. Defaults to the initial value of the target type.
 * @param recursionTracker A helper object to keep track of the recursion count.
 */
// TODO remove once we remove legacy import flow for map option
export function convertFromCsv(
  context: Optional<
    ValueWithContext<DatabaseFieldType, ObjectMode>,
    'database' | 'entry'
  >,
  newType: DatabaseFieldType,
  state: DefaultRootState | undefined,
  fallback: DatabaseValue<DatabaseFieldType> = getInitialValue(newType),
  recursionTracker = { recursionCount: 0 }
): DatabaseValue<DatabaseFieldType> {
  if (context.value.type === newType) {
    return context.value;
  } else if (untypedFieldTypes[newType].convertFromCsv) {
    return {
      type: newType,
      value: (untypedFieldTypes as any)[newType].convertFromCsv({
        ...context,
        value: context.value.value,
      }),
    };
  } else {
    return convertType(context, newType, state, fallback, recursionTracker);
  }
}

/**
 * Returns true if the given field type supports having a default value. Some types don't support default values at the moment because
 * of problems with copying attachments.
 */
export function fieldTypeSupportsDefaultValue(type: DatabaseFieldType): boolean {
  return !untypedFieldTypes[type].noDefaultValue;
}
